import { Button, Form, Input, InputNumber, InputRef, Modal, notification, Popconfirm, Row, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { ColumnType } from "antd/lib/table";
import React, { useCallback, useEffect, useMemo } from "react";
import { TbListDetails } from "react-icons/tb";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { filterFunction } from "../../../common/SearchFilterComponent";
import { ICNDNRequest, ICNRequestDetails, ISearchWaybillResponse } from "../../../model/CNDNModel";
import { useCNDNStore } from "../../../stores/CNDNStore";
import { CN_REQUEST_NUMBER, DD_MM_YYYY_H_MM, PRINT_OR_CANCEL_MESSAGE, VAT_NA_ID, dropDownMapping, isNotEmptyObject } from "../../../utils/app-common";
import { convertDateFromMillis } from "../../../utils/luxon";
import { amendAmountValidation, requiredValidation } from "../../../validation/validation";
import TableComponent from "../../CommonComponent/TableComponent";
import EditableCell from "../EditableCell";
import { useCreditNoteContextHook } from "./CreditNoteTableComponent";
import CreditNoteWaybillHistoryComponent from "./CreditNoteWaybillHistoryComponent";
import { SessionStorage } from "../../../services/storage";
import { useUserContextHook } from "../../../hooks/useUserContextHook";
import { useCommonStore } from "../../../stores/CommonStore";
import { ICommonState } from "../../../model/CommonModel";
import { FcCancel } from "react-icons/fc";

const PopupComponent = ({ cancel, record }: { cancel: (key: string) => Promise<void>, record: ISearchWaybillResponse }) => {
    return (
        <Popconfirm title="Sure to reset?" onConfirm={() => cancel(record?.waybillNo)}>
            <a>Reset</a>
        </Popconfirm>
    )
}

const DoneComponent = ({ save, record }: { save: (key: string) => Promise<void>, record: ISearchWaybillResponse }) => {
    return (
        <Typography.Link onClick={() => save(record?.waybillNo)} style={{ marginRight: 8 }}>
            Done
        </Typography.Link>
    )
}

const FailedComponent = () => {
    const creditNoteResponse = useCNDNStore(state => state?.creditNoteResponse);
    const { failedWayBillList } = creditNoteResponse || {};

    return (
        <>
            {
                failedWayBillList?.map(({ waybillNumber, message }) => <Typography.Text key={waybillNumber} strong type="danger">{waybillNumber} - the current status is {message?.toLocaleUpperCase()} </Typography.Text>)
            }
        </>
    )
}
const CreateCreditNoteTableComponent = () => {
    const searchInput = React.useRef<InputRef>(null);
    const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[] | undefined>([]);
    const [showHistoryModal, setShowHistoryModal] = React.useState(false);
    const [editingKey, setEditingKey] = React.useState<React.Key[] | undefined>(undefined);
    const [form] = Form.useForm();
    const selectedRowKeysSet = new Set(selectedRowKeys);
    const search = useCNDNStore(state => state?.search);
    const creditNoteResponse = useCNDNStore(state => state?.creditNoteResponse);
    const SaveCNDNAction = useCNDNStore(state => state?.SaveCNDNAction);
    const { promiseInProgress: CNSearchProgress } = usePromiseTracker({ area: 'cn-search-area' });
    const { promiseInProgress: CNSaveProgress } = usePromiseTracker({ area: 'cn-save-area' });
    const PrintCreditNoteAction = useCNDNStore(state => state?.PrintCreditNoteAction);
    const { promiseInProgress: printCreditNoteProgress } = usePromiseTracker({ area: 'get-cn-print-area' });
    const { failedWayBillList } = creditNoteResponse || {};
    const { selectedSubClientId, setSelectedWaybill } = useCreditNoteContextHook();
    const { employeeDetail } = useUserContextHook();
    const { countryId } = employeeDetail || {};
    const { vatTableList } = JSON.parse(SessionStorage.get('app-storage') ?? '{}');

    const vatTablesMemo = useMemo(() => dropDownMapping(vatTableList, 'id', 'vatName', 'vatPercentage', '%').filter(item => item?.countryId === countryId),
        [vatTableList, countryId]);
    const vatTableMap = useMemo(() => {
        const obj = {};
        vatTablesMemo?.forEach((item) => {
            obj[item?.id] = item?.value;
        });
        return obj;
    }, [vatTablesMemo]);


    const GetCnNoteReasonsAction = useCommonStore((state: ICommonState) => state.GetCnNoteReasonsAction);
    const cnNoteReasonsList = useCommonStore((state: ICommonState) => state?.cnNoteReasonsList);

    useEffect(() => {
        trackPromise(GetCnNoteReasonsAction(), 'cn-search-area');
    }, [GetCnNoteReasonsAction])

    const cnNoteReasonsListMemo = useMemo(() => dropDownMapping(cnNoteReasonsList, 'id', 'reason', 'vatPercentage', '%'), [cnNoteReasonsList]);

    const cnNoteReasonsMap = useMemo(() => {
        const obj = {};
        cnNoteReasonsListMemo?.forEach((item) => {
            obj[item?.id] = item?.value;
        });
        return obj;
    }, [cnNoteReasonsListMemo]);


    //TODO: Change to edit multiple.
    const isEditing = (record: ISearchWaybillResponse) => record?.waybillNo === (editingKey?.at(-1));
    //return editingKey?.includes(record?.waybillNo);

    const getColumnSearchProps = (dataIndex: string): ColumnType<any> => filterFunction(dataIndex, searchInput);
    const [data, setData] = React.useState<ISearchWaybillResponse[]>([]);
    const [oldGrossCharge, setOldGrossCharge] = React.useState<ISearchWaybillResponse[]>([]);

    React.useEffect(() => {
        if (search?.length) {
            setData(search)
        } else {
            setData([]);
        }
    }, [search]);

    const onConfirmationHandler = React.useCallback(() => {
        trackPromise(PrintCreditNoteAction(creditNoteResponse?.id), 'get-cn-print-area');
    }, [PrintCreditNoteAction, creditNoteResponse?.id]);

    React.useEffect(() => {
        if (isNotEmptyObject(creditNoteResponse)) {
            if (failedWayBillList?.length) {
                return;
            };
            Modal.info({
                title: 'Print & Close',
                cancelText: 'Cancel',
                content: `${CN_REQUEST_NUMBER} : ${creditNoteResponse?.cndnRequestNo}, ${PRINT_OR_CANCEL_MESSAGE} `,
                okText: 'Print & Close',
                onOk: onConfirmationHandler,
                onCancel: () => Modal.destroyAll(),
                closable: true,
                maskClosable: false,
            });
        }
    }, [creditNoteResponse, failedWayBillList?.length, onConfirmationHandler])
    const save = useCallback(async (key: string) => {
        try {
            const row = (await form.validateFields()) as ISearchWaybillResponse;


            if (row?.cnNoteReasonId === undefined && row?.amendVatPercentageId === undefined) { // cn vat and dn vat cannot both be null
                notification.open({
                    message: 'You must select credit note reason OR debit note amend VAT percentage',
                    style: {
                        color: 'rgba(0, 0, 0, 0.65)',
                        border: '1px solid #ffa39e',
                        backgroundColor: '#fff1f0'
                    },
                    icon: <FcCancel />
                });
                return;
            }

            const newData = [...data];
            const index = newData.findIndex(item => key === item?.waybillNo);
            if (index > -1) {
                const item = newData[index];
                newData.splice(index, 1, {
                    ...item,
                    ...((row?.amendmentAmount !== undefined && Number(row?.amendmentAmount) < 0)
                        ? {
                            amendVatAmount: (row?.amendmentAmount === undefined) || (row?.cnNoteReasonId === undefined) ? 0 : (Number(row?.amendmentAmount) * Number(cnNoteReasonsList?.find((el) => el?.id === row?.cnNoteReasonId)?.vatPercentage) / 100),
                            grossAmendCharge: (row?.amendmentAmount === undefined) || (row?.cnNoteReasonId === undefined) ? 0 : Number(row?.amendmentAmount) + (Number(row?.amendmentAmount) * Number(cnNoteReasonsList?.find((el) => el?.id === row?.cnNoteReasonId)?.vatPercentage) / 100),
                        }
                        : {
                    amendVatAmount: (row?.amendmentAmount === undefined ) || (row?.amendVatPercentageId === undefined) ? 0 : (Number(row?.amendmentAmount) * Number(vatTableList?.find((el) => el?.id === row?.amendVatPercentageId)?.vatPercentage) / 100),
                    grossAmendCharge: (row?.amendmentAmount === undefined ) || (row?.amendVatPercentageId === undefined) ? 0 : Number(row?.amendmentAmount) + (Number(row?.amendmentAmount) * Number(vatTableList?.find((el) => el?.id === row?.amendVatPercentageId)?.vatPercentage) / 100),
                            }
                    ),

                    ...row,
                });
                setData(newData);
                setEditingKey(undefined);
            } else {
                newData.push(row);
                setData(newData);
                setEditingKey(undefined);
            }
        } catch (errInfo) {
            console.error('Validate Failed:', errInfo);
        }
    }, [data, form]);

    const cancel = useCallback(async (key: string) => {
        setSelectedRowKeys(selectedRowKeys?.filter(item => item !== key));
        const newData = [...data];
        const index = newData.findIndex(item => key === item?.waybillNo);
        if (index > -1) {
            const item = search[index];
            newData.splice(index, 1, {
                ...item,
            });
            setData(newData);
            setEditingKey(undefined);
        };
    }, [data, selectedRowKeys, search])

    const onHistoryHandler = useCallback((waybillNo: string) => {
        setShowHistoryModal(true);
        setSelectedWaybill(waybillNo);
    }, [setSelectedWaybill])

    React.useEffect(() => {
        setOldGrossCharge([...data]);
    }, []);
    // const handleAmendmentAmountChange = (e: any | null, record: ISearchWaybillResponse) => {
    //     // Update the amendmentAmount value and set totalGross to the same value
    //     const updatedData = [...data];
    //     const index = updatedData.findIndex(item => item.id === record.id);

    //     if (index !== -1) {
    //         updatedData[index].amendmentAmount = e?.target?.value === null ? '' : String(e?.target?.value);
    //         updatedData[index].grossAmendCharge = Number(updatedData[index].amendmentAmount) + ((Number(updatedData[index].amendmentAmount) * Number(updatedData[index].vatPercentage)) / 100); // Set gross to the entered amendmentAmount
    //     }

    //     setData(updatedData);
    // };



    const columns: ColumnsType<ISearchWaybillResponse> = React.useMemo(() => [
        {
            title: <span style={{ fontSize: 9 }}>Invoice Number</span>,
            dataIndex: 'oracleInvoiceNumber',
            key: 'oracleInvoiceNumber',
            width: 70,
            // ...getColumnSearchProps('oracleInvoiceNumber'),
            editable: false,
        }, {
            title: <span style={{ fontSize: 9 }}>Waybill No</span>,
            dataIndex: 'waybillNo',
            key: 'waybillNo',
            width: 50,
            // ...getColumnSearchProps('waybillNo'),
            editable: false,
        },
        {
           title: <span style={{ fontSize: 9 }}>Customer Name</span>,
            dataIndex: 'customerName',
            key: 'customerName',
            width: 50,
            // ...getColumnSearchProps('customerName'),
            editable: false,
        },
        {
            title: <span style={{ fontSize: 9 }}>Customer Account</span>,
            dataIndex: 'customerSubAccountCode',
            key: 'customerSubAccountCode',
            width: 70,
            // ...getColumnSearchProps('customerSubAccountCode'),
            editable: false,
        },
        {
            title: <span style={{ fontSize: 9 }}>Industry Group</span>,
            dataIndex: 'industryGroup',
            key: 'industryGroup',
            width: 70,
            // ...getColumnSearchProps('industryGroup'),
            editable: false,
        },
        {
            title: <span style={{ fontSize: 9 }}>Delivery Date</span>,
            dataIndex: 'deliveryDate',
            width: 50,
            key: 'deliveryDate',
            // ...getColumnSearchProps('deliveryDate'),
            editable: false,
            render: (deliveryDate: number) => deliveryDate ? convertDateFromMillis(deliveryDate, DD_MM_YYYY_H_MM) : ''
        },
        //  {
        //     title: 'Gross Amount',
        //     width: 30,
        //     dataIndex: 'grossCharge',
        //     key: 'grossCharge',
        //     ...getColumnSearchProps('grossCharge'),
        //     editable: false,

        // }, 
        {
            title: <span style={{ fontSize: 9 }}>Gross Amount</span>,
            width: 50,
            dataIndex: 'grossCharge',
            key: 'grossCharge',
            // ...getColumnSearchProps('grossCharge'),
            editable: false
        },
        {
             title: <span style={{ fontSize: 9 }}>VAT Value</span>,
            width: 50,
            dataIndex: 'vatValue',
            key: 'vatValue',
            // ...getColumnSearchProps('vatValue'),
            editable: false,

        },
        {
            title: <span style={{ fontSize: 9 }}>VAT Percentage</span>,
            width: 50,
            dataIndex: 'vatPercentage',
            key: 'vatPercentage',
            // ...getColumnSearchProps('vatPercentage'),
            editable: false,

        },
        {
            title: <span style={{ fontSize: 9 }}>Int' Gross Amount</span>,
            width: 55,
            dataIndex: 'intGrossCharge',
            key: 'intGrossCharge',
            // ...getColumnSearchProps('intGrossCharge'),
            editable: false,
        },
        {
            title: <span style={{ fontSize: 9 }}>Int' VAT Value</span>,
            width: 55,
            dataIndex: 'intVatValue',
            key: 'intVatValue',
            // ...getColumnSearchProps('intVatValue'),
            editable: false,
        },
        {
            title: <span style={{ fontSize: 9 }}>Int' VAT Percentage</span>,
            width: 55,
            dataIndex: 'intVatPercentage',
            key: 'intVatPercentage',
            // ...getColumnSearchProps('intVatPercentage'),
            editable: false,
        },
        {
           title: <span style={{ fontSize: 9 }}>Total Gross</span>,
            width: 50,
            dataIndex: 'totalGross',
            key: 'totalGross',
            // ...getColumnSearchProps('totalGross'),
            editable: false,

        },
        {
             title: <span style={{ fontSize: 9 }}>Amendment Amount *</span>,
            width: 60,
            dataIndex: 'amendmentAmount',
            key: 'amendmentAmount',
            // ...getColumnSearchProps('amendmentAmount'),
            editable: true,
            maxLength: 10,
            required: true,
            validation: [{ ...requiredValidation, ...amendAmountValidation }],
        },
        {
            title: <span style={{ fontSize: 9 }}>Credit Note Reason & Amend VAT Percentage </span>,
            width: 200,
            dataIndex: 'cnNoteReasonId',
            key: 'cnNoteReasonId',
            editable: true,
            // required: true,
            allowClear: true,
            // validation: [requiredValidation],
            render: (cnNoteReasonId: number) => cnNoteReasonsMap[cnNoteReasonId?.toString()] ?? "",
        },
        {
            title: <span style={{ fontSize: 9 }}>Debit Note Amend VAT Percentage </span>,
            width: 120,
            dataIndex: 'amendVatPercentageId',
            key: 'amendVatPercentageId',
            editable: true,
            // required: true,
            // validation: [requiredValidation],
            allowClear: true,
            render: (amendVatPercentageId: number) => vatTableMap[amendVatPercentageId?.toString()] ?? "",
        },
        {
             title: <span style={{ fontSize: 9 }}>Amend VAT Amount</span>,
            width: 60,
            dataIndex: 'amendVatAmount',
            key: 'amendVatAmount',
            // ...getColumnSearchProps('amendVatAmount'),
            maxLength: 10,
        },
        {
            title: <span style={{ fontSize: 9 }}>Gross Amendment Amount</span>,
            width: 50,
            dataIndex: 'grossAmendCharge',
            key: 'grossAmendCharge',
            // ...getColumnSearchProps('grossAmendCharge'),
            editable: false,
            render: (grossAmendCharge: number) => grossAmendCharge ? grossAmendCharge : '0',
        },
        {
            title: <span style={{ fontSize: 9 }}>Remarks</span>,
            width: 60,
            dataIndex: 'remarks',
            key: 'remarks',
            // ...getColumnSearchProps('remarks'),
            editable: true,
            maxLength: 50,
        },
        {
           title: <span style={{ fontSize: 9 }}>operation</span>,
            dataIndex: 'operation',
            key: 'operation',
            width: 50,
            render: (_: string, record: ISearchWaybillResponse) => {
                //TODO :  multiple edit
                //editingKey?.includes(record?.waybillNo)
                return (record?.waybillNo === editingKey?.at(-1)) ? (
                    <DoneComponent save={save} record={record} />
                ) : (record?.amendmentAmount) ?
                    <PopupComponent cancel={cancel} record={record} />
                    : null
            }
        },
        {
            title: <span style={{ fontSize: 9 }}>Info</span>,
            width: 45,
            dataIndex: '',
            key: 'info',
            editable: false,
            render: (_text, record: ISearchWaybillResponse, _index) => {
                return (
                    <TbListDetails size={18} className='cursor-pointer' onClick={() => record && onHistoryHandler(record?.waybillNo)} />
                )
            }
        },
    ], [editingKey, onHistoryHandler, cancel, save]);

    const edit = (record: Partial<ISearchWaybillResponse[] | ISearchWaybillResponse>, selectedRowKeys?: React.Key[]) => {
        setEditingKey(selectedRowKeys);
        form.setFieldsValue({ amendmentAmount: undefined,  cnNoteReasonId: undefined, amendVatPercentageId: undefined, remarks: '', ...record });
    };

    const onFinishHandler = async () => {
        if (selectedRowKeys?.length) {
            const selectedRecord = data?.filter(record => selectedRowKeysSet.has(record?.waybillNo))
            const cnRequest: ICNRequestDetails[] = selectedRecord?.map((item: ISearchWaybillResponse) => ({
                waybillNo: item?.waybillNo,
                subClientId: selectedSubClientId ?? item?.subClientId,
                grossCharge: item?.grossCharge,
                amendCharge: item?.amendmentAmount,
                cnNoteReasonId: item?.cnNoteReasonId,
                amendVatPercentageId: item?.amendVatPercentageId,
                amendVatAmount: item?.amendVatAmount,
                grossAmendCharge: item?.grossAmendCharge,
                remarks: item?.remarks,
                oracleInvoiceNumber: item?.oracleInvoiceNumber,
            }))
            const request: ICNDNRequest = {
                subClientId: selectedSubClientId ?? selectedRecord?.at(0)?.subClientId,
                cnRequestDetailsList: cnRequest,
            }
            trackPromise(SaveCNDNAction(request), 'cn-save-area');
        }
    }

    const onSelectChange = (selectedRowKeys: React.Key[], selectedRows: ISearchWaybillResponse[]) => {
        edit(selectedRows, selectedRowKeys);
        setSelectedRowKeys(selectedRowKeys)
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const mergedColumns = columns.map((col: any) => {
        if (!col?.editable) {
            return col;
        }
        return {
            ...col,
            onCell: (record: ISearchWaybillResponse, rowIndex: number) => ({
                record,
                index: rowIndex,
                inputType: ['cnNoteReasonId','amendVatPercentageId'].includes(col?.dataIndex) ? 'select' : 'text',
                dropdownList: ['cnNoteReasonId'].includes(col?.dataIndex) ? cnNoteReasonsListMemo : ['amendVatPercentageId'].includes(col?.dataIndex) ? vatTablesMemo  : [],
                vatList: ['remarks'].includes(col?.dataIndex) ? vatTableList : [],
                dataIndex: col?.dataIndex,
                title: col?.title,
                validation: col?.validation,
                maxLength: col?.maxLength,
                allowClear: col?.allowClear,
                editing: isEditing(record),
            })
        };
    });

    return (
        <>

            <Row>
                {failedWayBillList?.length ? <FailedComponent /> : ''}
            </Row>
            <Form form={form} onFinish={onFinishHandler} autoComplete="off">
                <Row justify={'start'} align={'middle'}>
                    <Typography.Text strong type="danger">* Amendment amount is required </Typography.Text>
                </Row>
                <TableComponent
                    components={{
                        body: {
                            cell: EditableCell,
                        },
                    }}
                    rowKey='waybillNo'
                    scroll={{ x: 0, y: 0 }}
                    data={data ?? []}
                    columns={mergedColumns}
                    isLoading={CNSearchProgress || CNSaveProgress || printCreditNoteProgress}
                    rowClassName="editable-row"
                    rowSelected
                    showRowSelection
                    rowSelection={rowSelection}
                >
                    <Row justify={'end'} align={'middle'}>
                        <Button loading={CNSaveProgress} type='primary' htmlType='submit' disabled={!!(editingKey?.length)}>Save & Confirmed</Button>
                    </Row>
                </TableComponent>
            </Form>
            {/* {confirmationModal ?
                <ConfirmationModal openModal={confirmationModal}
                    title={'Success'}
                    promiseInProgress={printCreditNoteProgress}
                    hideModal={onCancelHandler}
                    confirmClicked={onConfirmationHandler}
                    label={confirmationLabel}
                    okText='Print & Close' />
                : null} */}
            {showHistoryModal ?
                <CreditNoteWaybillHistoryComponent open={showHistoryModal} onClose={(value) => setShowHistoryModal(value)} />
                : null}
        </>
    )
}
export default CreateCreditNoteTableComponent